import { useState } from 'react';
import { useSeo } from '@/hooks/useSeo';

const FORM_ACTION = 'https://readdy.ai/api/form/db7lj70mkl3spn8sma80';

const CHANNELS = [
  { icon: 'ri-mail-line', label: '邮箱反馈', value: 'hello@free91.example.com', note: '工作日 24 小时内回复' },
  { icon: 'ri-wechat-line', label: '微信公众号', value: '免费91资源站', note: '扫码关注获取更新' },
  { icon: 'ri-message-2-line', label: '在线留言', value: '填写右侧表单', note: '最快获得我们的帮助' },
];

export default function ContactPage() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useSeo({
    title: '联系我们 - 免费91',
    description:
      '通过邮箱或在线表单联系免费91，提交资源反馈、合作建议或版权问题，我们会尽快回复。',
    keywords: '联系免费91,反馈,资源建议,版权投诉',
  });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const name = String(formData.get('name') || '').trim();
    const email = String(formData.get('email') || '').trim();
    const message = String(formData.get('message') || '').trim();

    if (!name || !email || !message) {
      setStatus('error');
      setFormError('请完整填写姓名、邮箱和反馈内容。');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setFormError('邮箱格式不正确，请检查后重试。');
      return;
    }
    if (message.length > 500) {
      setStatus('error');
      setFormError('反馈内容不能超过 500 个字符。');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') body.append(key, value);
    });

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const succeeded = response.ok && parsed?.code === 'OK' && !isSpam;

      if (succeeded) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交内容被判定为异常，请修改后重试。'
            : serverMsg || '提交失败，请稍后重试。',
        );
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <div className="w-full max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
            <i className="ri-chat-3-line"></i>
            联系反馈
          </span>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-950 leading-tight">
            有建议或问题？随时告诉我们
          </h1>
          <p className="mt-4 text-foreground-600 leading-relaxed">
            无论是资源推荐、内容纠错，还是版权相关的问题，都欢迎通过下面的方式联系我们。
            你的每一条反馈，都会帮助免费91 变得更好。
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-4">
            {CHANNELS.map((channel) => (
              <div key={channel.label} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                  <i className={`${channel.icon} text-xl`}></i>
                </span>
                <p className="mt-4 text-xs text-foreground-500">{channel.label}</p>
                <p className="mt-1 font-heading font-semibold text-base text-foreground-950 break-all">
                  {channel.value}
                </p>
                <p className="mt-1 text-xs text-foreground-500">{channel.note}</p>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading font-semibold text-xl text-foreground-950">在线反馈表单</h2>
              <p className="mt-1.5 text-sm text-foreground-500">带 * 的为必填项，反馈内容限 500 字以内。</p>

              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="mt-6 space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      姓名 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      邮箱 <span className="text-accent-600">*</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="方便我们回复你"
                      className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    反馈内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请描述你的建议、问题或需要推荐的资源（500 字以内）"
                    className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                  />
                  <div className="mt-1.5 text-right text-xs text-foreground-400">最多 500 字</div>
                </div>

                <input
                  className="f91-reqfield-guard"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="h-11 px-7 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium flex items-center justify-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                    {status === 'submitting' ? '提交中…' : '提交反馈'}
                  </button>
                  <p className="text-xs text-foreground-500">我们会在工作日尽快回复你的反馈。</p>
                </div>

                {status === 'success' && (
                  <div className="flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3 text-sm text-primary-800 f91-fade-in">
                    <i className="ri-checkbox-circle-line text-base mt-0.5"></i>
                    <span>提交成功，感谢你的反馈！我们会尽快与你联系。</span>
                  </div>
                )}
                {status === 'error' && formError && (
                  <div className="flex items-start gap-2 rounded-md bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-800 f91-fade-in">
                    <i className="ri-error-warning-line text-base mt-0.5"></i>
                    <span>{formError}</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}